import {
  Action,
  config,
  defaultProcessingSaga,
  defaultResponseProcessing,
  fetchAuthSaga,
  FetchResponse,
} from '@triare/auth-redux';
import { put, delay, takeLatest, call } from 'redux-saga/effects';
import { actions, Post, PostStatus } from './index';
import { PaginatedResponse, PaginationProps } from '../types';

export type PostListData = PaginatedResponse<Post>;

export type PostListResponse = PostListData;
export type PostOrderColumn =
  | 'id'
  | 'text'
  | 'numberOfLikes'
  | 'numberOfDislikes'
  | 'numberOfPosts'
  | 'createdAt'
  | 'updatedAt';
export type PostEntity =
  | 'business'
  | 'article'
  | 'international_community'
  | 'category'
  | 'subcategory'
  | 'post';
export const PostEntityTitle: Record<PostEntity, string> = {
  business: 'Business',
  article: 'Article',
  international_community: 'International community',
  category: 'Category',
  subcategory: 'Subcategory',
  post: 'Post',
};

export type PostListParams = PaginationProps<PostOrderColumn> & {
  text?: string;
  categoryId?: string;
  subCategoryId?: string;
  article?: string;
  businessId?: string;
  businessName?: string;
  businessZipCode?: string;
  businessCity?: string;
  businessState?: string;
  businessCountry?: string;
  user?: string;
  entity?: PostEntity | null;
  status?: PostStatus | null;
  search?: string;
};

export const postList = (payload: PostListParams): Action<PostListParams> => ({
  type: actions.postListTrigger.toString(),
  payload,
});

export const postListClear = (): Action => ({
  type: actions.postListClear.toString(),
});

export const postListErrorClear = (): Action => ({
  type: actions.postListErrorReset.toString(),
});

export function* postListRequestSaga({ payload }: Action<PostListParams>) {
  yield put({
    type: actions.postListStart.toString(),
  });

  if (!payload) {
    yield put({
      type: actions.postListError.toString(),
      payload: {
        message: 'PostList: Parameters not passed.',
      },
    });

    return;
  }

  const { roles, ...restParams } = payload as Record<string, string>;

  const params = new URLSearchParams(
    Object.entries(restParams).filter((entry) => !!(entry[1] as string))
  );

  const response = (yield call(
    fetchAuthSaga,
    `${process.env.REACT_APP_API}posts?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    {},
    config.fetchDelay
  )) as FetchResponse;

  yield defaultResponseProcessing<PostListData, PostListResponse>(
    response,
    actions.postListSuccess.toString(),
    actions.postListError.toString(),
    false,
    (postListData) => postListData
  );
}

export default function* saga() {
  yield takeLatest(
    actions.postListTrigger.toString(),
    defaultProcessingSaga(postListRequestSaga, {
      error: actions.postListError.toString(),
      locationCancel: actions.postListClear.toString(),
    })
  );

  yield takeLatest(actions.postListSuccess.toString(), function* clear() {
    if (config.delaySuccess) {
      yield delay(config.delaySuccess);
    }
  });

  yield takeLatest(actions.postListError.toString(), function* errorReset() {
    if (config.delayError) {
      yield delay(config.delayError);
    }

    yield put({
      type: actions.postListErrorReset.toString(),
    });
  });
}
